import { isNil } from "lodash"
import useQueryParams from "magik-react-hooks/useRouterDebounceQueryParams"
import { useTranslation } from "react-i18next"
import { Card, CardBody, CardDeck, UncontrolledTooltip } from "reactstrap"
import { IndicatorsLandmarks } from "../../components/IndicatorsLandmarks"
import Layout from "../../components/Layout"
import WpInput from "../../components/WpInput"
import { useIndicators } from "../../hooks/indicators"
import { useAnalysisFilters } from "../../hooks/useAnalysisFilters"
import { BsInfoCircleFill } from "react-icons/bs"

function IndicatorCard({ name, value }) {
  const { t } = useTranslation()
  return (
    <Card size="sm">
      <CardBody className="text-center">
        <div className="d-flex flex-row justify-content-center align-items-center mb-5" style={{ columnGap: 8 }}>
          <p className="m-0">{t("indicators." + name)}</p>
          <UncontrolledTooltip placement="bottom" target={"info-" + name}>
            {t("indicators.explain." + name)}
          </UncontrolledTooltip>
          <BsInfoCircleFill id={"info-" + name} className="pointer" />
        </div>
        <h1>{value}</h1>
      </CardBody>
    </Card>
  )
}

function EmptyCard() {
  return (
    <Card size="sm">
      <CardBody className="text-center">
      </CardBody>
    </Card>
  )
}

export function Indicators() {
  const { t } = useTranslation()
  const { filters, renderFilters } = useAnalysisFilters()

  const [{ fromDate, toDate }, , { fromDate: debFromDate, toDate: debToDate }, setDebQueryParams] = useQueryParams()

  const [{ data }] = useIndicators(debFromDate, debToDate, filters)

  return (
    <Layout>
      <div className="container-fluid pt-8 px-page pb-page-bottom">
        <h1 className="text-primary font-weight-semibold">{t("indicators.title")}</h1>
        <div className="d-flex flex-row justify-content-start align-items-start">
          <div
            className="d-flex flex-row justify-content-start align-items-center flex-1 flex-wrap"
            style={{ rowGap: 16, columnGap: 32 }}
          >
            <div style={{ whiteSpace: "nowrap" }}>
              <span className="mr-3">{t("indicators.period_filter_from")}</span>
              <WpInput
                medium
                type="date"
                value={fromDate}
                onChange={(e) => setDebQueryParams({ fromDate: e.target.value, page: 1 })}
              />
              <span className="mx-3">{t("indicators.period_filter_to")}</span>
              <WpInput
                medium
                type="date"
                value={toDate}
                onChange={(e) => setDebQueryParams({ toDate: e.target.value, page: 1 })}
              />
            </div>
            <div style={{ whiteSpace: "nowrap" }}>{renderFilters()}</div>
          </div>
        </div>
        {data && (
          <>
            <CardDeck className="mt-9">
              <IndicatorCard name="billed_amount" value={formatEuro(data.billed_amount)} />
              <IndicatorCard name="amount_to_bill" value={formatEuro(data.amount_to_bill)} />
              <IndicatorCard name="amount_to_bill_potential" value={formatEuro(data.amount_to_bill_potential)} />
            </CardDeck>
            <CardDeck className="mt-9">
              <IndicatorCard name="approved_estimates_amount" value={formatEuro(data.approved_estimates_amount)} />
              <IndicatorCard name="generated_estimates_amount" value={formatEuro(data.generated_estimates_amount)} />
              <IndicatorCard name="average_project_margin" value={formatPercent(data.average_project_margin)} />
            </CardDeck>
            <CardDeck className="mt-7">
              <IndicatorCard name="average_estimate_time" value={formatDays(data.average_estimate_time, t)} />
              <IndicatorCard name="won_contests_percent" value={formatPercent(data.won_contests_percent)} />
              <IndicatorCard name="customer_number" value={data.customer_number} />
            </CardDeck>
            <CardDeck className="mt-7">
              <IndicatorCard name="new_customer_number" value={data.new_customer_number} />
              <IndicatorCard name="accepted_estimate_value_percent" value={formatPercent(data.accepted_estimate_value_percent)} />
              <IndicatorCard name="accepted_estimate_count_percent" value={formatPercent(data.accepted_estimate_count_percent)} />
              {/* <EmptyCard /> */}
            </CardDeck>
          </>
        )}
      </div>
      <Layout.FirstLevelNavi>
        <IndicatorsLandmarks />
      </Layout.FirstLevelNavi>
    </Layout>
  )
}

function formatEuro(value) {
  if (isNil(value)) {
    return <i>Non calcolabile</i>
  } else {
    return <span>{parseFloat(value).toFixed(2) + " €"}</span>
  }
}

function formatDays(value, t) {
  if (isNil(value)) {
    return <i>Non calcolabile</i>
  } else {
    return <span>{parseFloat(value).toFixed(2) + " " + t("days")}</span>
  }
}

function formatPercent(value) {
  if (isNil(value)) {
    return <i>Non calcolabile</i>
  } else {
    return <span>{parseFloat(value).toFixed(2) + " %"}</span>
  }
}
